import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import './Accounting.css';


const TABS = ['Day Book', 'Cash Book', 'Customer Outstanding', 'Supplier Outstanding'];
const TAB_LABEL_KEYS = {
  'Day Book': 'dayBook',
  'Cash Book': 'cashBook',
  'Customer Outstanding': 'customerOutstanding',
  'Supplier Outstanding': 'supplierOutstanding',
};

export default function Accounting({ subPage }) {
  const { t: tr } = useTranslation();
  const { can } = useAuth();
  const [tab, setTab] = React.useState(subPage || 'Day Book');

  React.useEffect(() => {
    if (subPage) setTab(subPage);
  }, [subPage]);
  if (!can('accounting', 'read')) {
    return (
      <div style={{ padding: 20 }}>
        {tr('common.accessDenied')}
      </div>
    );
  }

  return (
    <div className="accounting-page">
      <div className="page-header">
        <h2 className="page-title">{tr('accounting.title')}</h2>
      </div>

      <div className="acc-tabs">
        {TABS.map(t => (
          <button
            key={t}
            className={`acc-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >{tr('accounting.tabs.' + TAB_LABEL_KEYS[t])}</button>
        ))}
      </div>

      <div className="acc-body">
        {tab === 'Day Book' && <DayBook />}
        {tab === 'Cash Book' && <CashBook />}
        {tab === 'Customer Outstanding' && <CustomerOutstanding />}
        {tab === 'Supplier Outstanding' && <SupplierOutstanding />}
      </div>
    </div>
  );
}

const inr = (n) => '₹ ' + fmtNum(n);
const refLabel = (r) => {
  const map = {
    sale: 'Sale', lube_sale: 'Lube Sale', purchase: 'Purchase',
    bill_receipt: 'Bill Receipt', daily_receipt: 'Daily Receipt',
    credit_collect: 'Credit Collect', item_loss: 'Item Loss',
    manual: 'Manual'
  };
  return map[r] || r;
};

/* ═══════════════════════════════════════════════════════════
   1. DAY BOOK — raw ledger entries in a date range
═══════════════════════════════════════════════════════════ */
function DayBook() {
  const { t: tr } = useTranslation();
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const { data, loading, error, reload } = useApi(
    () => api.ledger.daybook(from, to), [from, to], { rows: [], total_debit: 0, total_credit: 0 }
  );

  return (
    <div className="acc-section">
      <div className="acc-filters">
        <div className="form-group">
          <label>{tr('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>{tr('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>{tr('common.refresh')}</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="acc-kpis">
            <Kpi label={tr('accounting.totalDebit')} value={inr(data.total_debit)} tone="green" />
            <Kpi label={tr('accounting.totalCredit')} value={inr(data.total_credit)} tone="blue" />
            <Kpi label={tr('accounting.entries')} value={data.rows.length} />
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">{tr('accounting.dayBookRange', { from, to })}</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.type')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.account')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.party')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.reference')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.debit')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.credit')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.narration')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('accounting.noEntries')}</td></tr>
                ) : (data.rows || []).map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left' }}>{r.entry_date}</td>
                    <td style={{ textAlign: 'left' }}>
                      <span className={`pill ${r.type === 'Debit' ? 'pill-dr' : 'pill-cr'}`}>{r.type}</span>
                    </td>
                    <td style={{ textAlign: 'left' }}>{r.account_type}</td>
                    <td style={{ textAlign: 'left' }}>{r.party_name || '—'}</td>
                    <td style={{ textAlign: 'left' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{refLabel(r.reference_type)}</span>
                      {r.reference_no && <> · <strong>{r.reference_no}</strong></>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.type === 'Debit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.type === 'Credit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.narration}</td>
                  </tr>
                ))}
              </tbody>
              {(data.rows || []).length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700, background: '#f5f9fc' }}>
                    <td colSpan="5" style={{ textAlign: 'right' }}>{tr('accounting.totals')}</td>
                    <td style={{ textAlign: 'right' }}>{inr(data.total_debit)}</td>
                    <td style={{ textAlign: 'right' }}>{inr(data.total_credit)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   2. CASH BOOK — cash/bank movement on a single date
═══════════════════════════════════════════════════════════ */
function CashBook() {
  const { t: tr } = useTranslation();
  const [date, setDate] = useState(today());
  const { data, loading, error, reload } = useApi(
    () => api.ledger.cashbook(date), [date],
    {
      receipts: [], payments: [], opening_cash: 0, opening_bank: 0,
      total_in_cash: 0, total_in_bank: 0, total_out_cash: 0, total_out_bank: 0,
      closing_cash: 0, closing_bank: 0
    }
  );

  return (
    <div className="acc-section">
      <div className="acc-filters">
        <div className="form-group">
          <label>{tr('common.date')}</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>{tr('common.refresh')}</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="acc-kpis">
            <Kpi label={tr('accounting.openingCash')} value={inr(data.opening_cash)} />
            <Kpi label={tr('accounting.openingBank')} value={inr(data.opening_bank)} />
            <Kpi label={tr('accounting.closingCash')} value={inr(data.closing_cash)} tone="green" />
            <Kpi label={tr('accounting.closingBank')} value={inr(data.closing_bank)} tone="blue" />
          </div>

          <div className="cashbook-grid">
            <div className="section-card" style={{ overflowX: 'auto' }}>
              <div className="section-header" style={{ background: '#e6f9ee', color: '#1e6e3b' }}>
                {tr('accounting.receiptsMoneyIn')}
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.source')}</th>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.party')}</th>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.mode')}</th>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.ref')}</th>
                    <th style={{ textAlign: 'right' }}>{tr('common.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.receipts.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('accounting.noReceipts')}</td></tr>
                  ) : data.receipts.map(r => (
                    <tr key={r.id}>
                      <td style={{ textAlign: 'left' }}>{refLabel(r.reference_type)}</td>
                      <td style={{ textAlign: 'left' }}>{r.party_name || '—'}</td>
                      <td style={{ textAlign: 'left' }}>{r.mode || r.account_type}</td>
                      <td style={{ textAlign: 'left' }}>{r.reference_no || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{inr(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, background: '#f5f9fc' }}>
                    <td colSpan="4" style={{ textAlign: 'right' }}>{tr('accounting.cashIn')}</td>
                    <td style={{ textAlign: 'right' }}>{inr(data.total_in_cash)}</td>
                  </tr>
                  <tr style={{ fontWeight: 700, background: '#f5f9fc' }}>
                    <td colSpan="4" style={{ textAlign: 'right' }}>{tr('accounting.bankIn')}</td>
                    <td style={{ textAlign: 'right' }}>{inr(data.total_in_bank)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="section-card" style={{ overflowX: 'auto' }}>
              <div className="section-header" style={{ background: '#fff3e6', color: '#a0531a' }}>
                {tr('accounting.paymentsMoneyOut')}
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.source')}</th>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.party')}</th>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.mode')}</th>
                    <th style={{ textAlign: 'left' }}>{tr('accounting.ref')}</th>
                    <th style={{ textAlign: 'right' }}>{tr('common.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('accounting.noPayments')}</td></tr>
                  ) : data.payments.map(r => (
                    <tr key={r.id}>
                      <td style={{ textAlign: 'left' }}>{refLabel(r.reference_type)}</td>
                      <td style={{ textAlign: 'left' }}>{r.party_name || '—'}</td>
                      <td style={{ textAlign: 'left' }}>{r.mode || r.account_type}</td>
                      <td style={{ textAlign: 'left' }}>{r.reference_no || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{inr(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, background: '#f5f9fc' }}>
                    <td colSpan="4" style={{ textAlign: 'right' }}>{tr('accounting.cashOut')}</td>
                    <td style={{ textAlign: 'right' }}>{inr(data.total_out_cash)}</td>
                  </tr>
                  <tr style={{ fontWeight: 700, background: '#f5f9fc' }}>
                    <td colSpan="4" style={{ textAlign: 'right' }}>{tr('accounting.bankOut')}</td>
                    <td style={{ textAlign: 'right' }}>{inr(data.total_out_bank)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   3. CUSTOMER OUTSTANDING — drill-down to ledger
═══════════════════════════════════════════════════════════ */
function CustomerOutstanding() {
  const { t: tr } = useTranslation();
  const [selected, setSelected] = useState(null);
  const { data, loading, error, reload } = useApi(
    () => api.ledger.customerOutstanding(), [], { rows: [], total_outstanding: 0 }
  );

  if (selected) {
    return <CustomerLedger customerId={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="acc-section">
      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="acc-kpis">
            <Kpi label={tr('accounting.totalOutstanding')} value={inr(data.total_outstanding)} tone="red" />
            <Kpi label={tr('accounting.customersWithBalance')} value={data.rows.filter(r => Number(r.outstanding) > 0).length} />
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">{tr('accounting.customerOutstanding')}</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.customer')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.gstin')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.phone')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.creditLimit')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.totalBilled')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.totalPaid')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.outstanding')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('accounting.noCustomers')}</td></tr>
                ) : data.rows.map(r => (
                  <tr key={r.customer_id}>
                    <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.customer_name}</td>
                    <td style={{ textAlign: 'left' }}>{r.gstin || '—'}</td>
                    <td style={{ textAlign: 'left' }}>{r.phone || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{inr(r.credit_limit)}</td>
                    <td style={{ textAlign: 'right' }}>{inr(r.total_debit)}</td>
                    <td style={{ textAlign: 'right' }}>{inr(r.total_credit)}</td>
                    <td style={{ textAlign: 'right', color: Number(r.outstanding) > 0 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: 700 }}>
                      {inr(r.outstanding)}
                    </td>
                    <td>
                      <button className="btn btn-primary btn-sm" onClick={() => setSelected(r.customer_id)}>
                        {tr('accounting.viewLedger')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CustomerLedger({ customerId, onBack }) {
  const { t: tr } = useTranslation();
  const { data, loading, error } = useApi(
    () => api.ledger.customerLedger(customerId), [customerId],
    { customer: {}, rows: [], final_balance: 0 }
  );

  return (
    <div className="acc-section">
      <button className="btn btn-sm" onClick={onBack} style={{ marginBottom: 10 }}>
        {tr('accounting.backToOutstanding')}
      </button>
      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="section-card">
            <div className="section-header">{tr('accounting.nameLedger', { name: data.customer.name })}</div>
            <div style={{ padding: '10px 14px', display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12.5 }}>
              <span><strong>{tr('accounting.gstin')}:</strong> {data.customer.gstin || '—'}</span>
              <span><strong>{tr('accounting.phone')}:</strong> {data.customer.phone || '—'}</span>
              <span><strong>{tr('accounting.creditLimit')}:</strong> {inr(data.customer.credit_limit)}</span>
              <span style={{ color: 'var(--danger)', fontWeight: 700 }}>
                {tr('accounting.currentBalance')}: {inr(data.final_balance)}
              </span>
            </div>
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.reference')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.narration')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.debit')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.credit')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('accounting.noTransactions')}</td></tr>
                ) : data.rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left' }}>{r.entry_date}</td>
                    <td style={{ textAlign: 'left' }}>{refLabel(r.reference_type)} {r.reference_no && `· ${r.reference_no}`}</td>
                    <td style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.narration}</td>
                    <td style={{ textAlign: 'right' }}>{r.type === 'Debit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.type === 'Credit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{inr(r.running_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   4. SUPPLIER OUTSTANDING
═══════════════════════════════════════════════════════════ */
function SupplierOutstanding() {
  const { t: tr } = useTranslation();
  const [selected, setSelected] = useState(null);
  const { data, loading, error } = useApi(
    () => api.ledger.supplierOutstanding(), [], { rows: [], total_outstanding: 0 }
  );

  if (selected) {
    return <SupplierLedger supplierId={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="acc-section">
      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="acc-kpis">
            <Kpi label={tr('accounting.weOweTotal')} value={inr(data.total_outstanding)} tone="red" />
            <Kpi label={tr('accounting.activeSuppliers')} value={data.rows.length} />
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">{tr('accounting.supplierOutstanding')}</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.supplier')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.gstin')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.contact')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.totalBilledCr')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.totalPaidDr')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.weOwe')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('accounting.noSuppliers')}</td></tr>
                ) : data.rows.map(r => (
                  <tr key={r.supplier_id}>
                    <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.supplier_name}</td>
                    <td style={{ textAlign: 'left' }}>{r.gstin || '—'}</td>
                    <td style={{ textAlign: 'left' }}>{r.contact || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{inr(r.total_credit)}</td>
                    <td style={{ textAlign: 'right' }}>{inr(r.total_debit)}</td>
                    <td style={{ textAlign: 'right', color: Number(r.outstanding) > 0 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: 700 }}>
                      {inr(r.outstanding)}
                    </td>
                    <td>
                      <button className="btn btn-primary btn-sm" onClick={() => setSelected(r.supplier_id)}>
                        {tr('accounting.viewLedger')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SupplierLedger({ supplierId, onBack }) {
  const { t: tr } = useTranslation();
  const { data, loading, error } = useApi(
    () => api.ledger.supplierLedger(supplierId), [supplierId],
    { supplier: {}, rows: [], final_balance: 0 }
  );

  return (
    <div className="acc-section">
      <button className="btn btn-sm" onClick={onBack} style={{ marginBottom: 10 }}>
        {tr('accounting.backToOutstanding')}
      </button>
      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="section-card">
            <div className="section-header">{tr('accounting.nameLedger', { name: data.supplier.name })}</div>
            <div style={{ padding: '10px 14px', display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12.5 }}>
              <span><strong>{tr('accounting.gstin')}:</strong> {data.supplier.gstin || '—'}</span>
              <span><strong>{tr('accounting.contact')}:</strong> {data.supplier.contact || '—'}</span>
              <span style={{ color: 'var(--danger)', fontWeight: 700 }}>
                {tr('accounting.weOwe')}: {inr(data.final_balance)}
              </span>
            </div>
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.reference')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('accounting.narration')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.debitPaid')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('accounting.creditBilled')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('accounting.noTransactions')}</td></tr>
                ) : data.rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left' }}>{r.entry_date}</td>
                    <td style={{ textAlign: 'left' }}>{refLabel(r.reference_type)} {r.reference_no && `· ${r.reference_no}`}</td>
                    <td style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.narration}</td>
                    <td style={{ textAlign: 'right' }}>{r.type === 'Debit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.type === 'Credit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{inr(r.running_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Tiny KPI card used across all tabs ───────────────────── */
function Kpi({ label, value, tone }) {
  const toneClass = tone ? `kpi-${tone}` : '';
  return (
    <div className={`acc-kpi ${toneClass}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
